import {
  Fragment,
  ReactElement,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import classNames from "classnames";
import { useRouter } from "next/router";

import {
  ChallengeSubmissionResponse,
  ChallengeSubmissionList,
  ChallengeSubmissionListItem,
  ChallengeSubmissionListItemKeys,
  ChallengeSubmissionListRequest,
  ChallengeSubmissionScoreRequest,
  ChallengeSubmissionScoreResponse,
} from "@ascendnow/shared";
import AscTable from "../AscTable";
import AscButton from "../../AscButton/AscButton";
import { formatErrorMessage } from "../../../lib";
import { useAscContext } from "../../../context";
import { AdminMedal1, AdminMedal2, AdminMedal3 } from "../../AscIcons";
import AscChallengeCheckEssayModal from "./AscChallengeCheckEssay";
import AscToogleBox from "../../AscInputFields/AscToogleBox/AscToogleBox";
import styles from "./AscChallengeStudentList.module.scss";

export interface AscChallengeStudentsListProps {
  title?: string;
  postSubmissionScoreApi: (
    props: ChallengeSubmissionScoreRequest
  ) => Promise<ChallengeSubmissionScoreResponse>;
  getChallengeSubmissionListApi: (
    props: ChallengeSubmissionListRequest
  ) => Promise<ChallengeSubmissionList>;
  getChallengeSubmissionApi: (
    submissionId: string
  ) => Promise<ChallengeSubmissionResponse>;
}

export interface IMyColumn {
  Header: () => ReactElement;
  accessor: string;
  enableSorting?: boolean;
  Cell?: (cell: { value: any }) => ReactElement;
}

export default function AscChallengeStudentsList({
  title,
  postSubmissionScoreApi,
  getChallengeSubmissionListApi,
  getChallengeSubmissionApi,
}: AscChallengeStudentsListProps) {
  const router = useRouter();
  const { toast } = useAscContext();
  const studentName = router.query.studentName as string;
  const sortBy = router.query.sortBy as string;
  const sortOrder = router.query.sortOrder as string;

  const [essayModal, setEssayModal] = useState<string | boolean>(false);
  const [submissionDetails, setSubmissionDetails] =
    useState<ChallengeSubmissionResponse>({
      submission: null,
      files: [],
      challenge: null,
    });
  const [studentsData, setStudentsData] = useState<ChallengeSubmissionList>([]);

  function getSubmissionDetails(submissionId: string) {
    getChallengeSubmissionApi(submissionId)
      .then((res) => setSubmissionDetails(res))
      .catch((err) => console.log({ err }));
  }

  const getChallengeSubmissionList = useCallback(
    async ({
      search,
      pastWinners = false,
      sortBy,
      sortOrder,
    }: ChallengeSubmissionListRequest) => {
      try {
        const submissions = await getChallengeSubmissionListApi({
          search,
          pastWinners,
          sortBy,
          sortOrder,
        });

        setStudentsData(submissions);
      } catch (error) {
        formatErrorMessage(
          error,
          `An error occurred while fetching student list of challenge submissions`
        );
      }
    },
    [getChallengeSubmissionListApi]
  );

  const handleClickApprove = async (
    submissionId: string,
    score: number,
    cb?: () => void
  ) => {
    try {
      const res = await postSubmissionScoreApi({ score, submissionId });
      if (res && res.id) {
        cb?.();
        getChallengeSubmissionList({ sortBy, sortOrder, search: studentName });
      }
    } catch (error) {
      toast?.error(
        formatErrorMessage(
          error,
          `An error occurred while giving points to the essay submission: ${submissionId}`
        )
      );
    }
  };

  useEffect(() => {
    getChallengeSubmissionList({ sortBy, sortOrder, search: studentName });
  }, [sortBy, sortOrder, studentName, getChallengeSubmissionList]);

  const tableData = useMemo(() => [...studentsData], [studentsData]);

  const tableColumns = [
    {
      Header: () => {
        return <div className={styles.header}>{"Place"}</div>;
      },
      accessor: ChallengeSubmissionListItemKeys.place,
      enableSorting: true,

      Cell: (cell: { value: number }) => {
        if (cell.value === 1) {
          return (
            <AdminMedal1
              className={classNames(styles.rankIcon, styles.medal1)}
            />
          );
        } else if (cell.value === 2) {
          return (
            <AdminMedal2
              className={classNames(styles.rankIcon, styles.medal2)}
            />
          );
        } else if (cell.value === 3) {
          return (
            <AdminMedal3
              className={classNames(styles.rankIcon, styles.medal3)}
            />
          );
        } else {
          return <span className={styles.rankText}>{cell.value}</span>;
        }
      },
    },
    {
      Header: () => {
        return <div className={styles.header}>Name</div>;
      },
      accessor: ChallengeSubmissionListItemKeys.name,
      enableSorting: true,
    },
    {
      Header: () => {
        return <div className={styles.header}>School</div>;
      },
      accessor: ChallengeSubmissionListItemKeys.school,
      enableSorting: true,
    },
    {
      Header: () => {
        return <div className={styles.header}>Challenge</div>;
      },
      accessor: ChallengeSubmissionListItemKeys.challengeTitle,
      enableSorting: true,
    },
    {
      Header: () => {
        return <div className={styles.header}>Writing</div>;
      },
      accessor: ChallengeSubmissionListItemKeys.id,
      Cell: (cell: { value: string }) => {
        return (
          <AscButton
            type="button"
            size="small"
            onClick={() => {
              setEssayModal(cell.value);
              getSubmissionDetails(cell.value);
            }}
          >
            Check essay
          </AscButton>
        );
      },
    },
    {
      Header: () => {
        return <div className={styles.header}>Status</div>;
      },
      accessor: "Active",
      enableSorting: true,
    },
    {
      Header: () => {
        return <div className={styles.header}>Points</div>;
      },
      accessor: ChallengeSubmissionListItemKeys.points,
      enableSorting: true,
      Cell: (cell: { value: number }) => <div>{cell.value || 0}</div>,
    },
  ];

  return (
    <Fragment>
      <div className={styles.table}>
        <span className={styles.title}>{title}</span>

        <AscTable
          tHead={styles.tHead}
          myTable={styles.myTable}
          tHeadRow={styles.tHeadRow}
          tH={styles.tH}
          theadCol={styles.theadCol}
          tBody={styles.tBody}
          tRow={styles.tRow}
          pureCellFlow={styles.pureCellFlow}
          tD={styles.tD}
          tBodyValue={styles.tBodyValue}
          tableColumns={tableColumns}
          tableData={tableData}
          showHeader={true}
          showPagination={false}
        />
        <div className={styles.pastWinnersTrigger}>
          <AscToogleBox
            id="past-winners"
            onChange={(e) =>
              getChallengeSubmissionList({
                search: studentName,
                sortBy,
                sortOrder,
                pastWinners: e.target.checked,
              })
            }
          />
          <label className={styles.pastWinnersLabel} htmlFor="past-winners">
            Past winners
          </label>
        </div>
      </div>
      {essayModal && (
        <AscChallengeCheckEssayModal
          {...{
            essayModal,
            setEssayModal,
            onClickApprove: handleClickApprove,
            selectedEssay: submissionDetails,
            score:
              studentsData.find(
                (el: ChallengeSubmissionListItem) => el.id === essayModal
              )?.score || 0,
          }}
        />
      )}
    </Fragment>
  );
}
